/**
 * SPF Optimization Wizard Component
 *
 * Multi-step wizard for AI-powered SPF record optimization.
 * Flow: Analysis → Review → Apply → Confirmation
 *
 * Tier Requirements: Pro+ (requires ai_features)
 */

import { useState, useEffect, useCallback } from "react";
import { Modal } from "@/components/ui/Modal";
import {
  X,
  Sparkles,
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Copy,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Zap,
  FileText,
  ArrowRight,
  Info,
  Minus,
  Plus,
} from "lucide-react";
import { api, SpfResolveResult, SpfOptimizationResult } from "@/lib/api";

type WizardStep = "analysis" | "review" | "apply" | "confirmation";

interface SpfOptimizationWizardProps {
  domainId: string;
  domainName: string;
  currentSpf: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

// Risk level colors and labels
const RISK_CONFIG = {
  safe: {
    color: "bg-emerald-100 text-emerald-700 border-emerald-200",
    bgColor: "bg-emerald-50",
    icon: CheckCircle2,
    label: "Safe",
    description: "Low risk changes - primarily additions and reordering",
  },
  caution: {
    color: "bg-amber-100 text-amber-700 border-amber-200",
    bgColor: "bg-amber-50",
    icon: AlertTriangle,
    label: "Caution",
    description: "Moderate changes including removals - review carefully",
  },
  risky: {
    color: "bg-red-100 text-red-700 border-red-200",
    bgColor: "bg-red-50",
    icon: XCircle,
    label: "Risky",
    description: "Significant changes - monitor closely after applying",
  },
};

// Change type colors
const CHANGE_TYPE_CONFIG: Record<string, { color: string; bgColor: string; icon: typeof Plus; label: string }> = {
  added: { color: "text-emerald-700", bgColor: "bg-emerald-50", icon: Plus, label: "Added" },
  removed: { color: "text-red-600", bgColor: "bg-red-50", icon: Minus, label: "Removed" },
  modified: { color: "text-amber-600", bgColor: "bg-amber-50", icon: RefreshCw, label: "Modified" },
  consolidated: { color: "text-purple-600", bgColor: "bg-purple-50", icon: Zap, label: "Consolidated" },
  reordered: { color: "text-blue-500", bgColor: "bg-blue-50", icon: ArrowRight, label: "Reordered" },
  qualifier_change: { color: "text-purple-500", bgColor: "bg-purple-50", icon: Shield, label: "Security" },
};

// Default config for unknown change types
const DEFAULT_CHANGE_CONFIG = { color: "text-slate-600", bgColor: "bg-slate-50", icon: Info, label: "Changed" };

export function SpfOptimizationWizard({
  domainId,
  domainName,
  currentSpf,
  onClose,
  onSuccess,
}: SpfOptimizationWizardProps) {
  const [step, setStep] = useState<WizardStep>("analysis");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedData, setResolvedData] = useState<SpfResolveResult | null>(null);
  const [optimization, setOptimization] = useState<SpfOptimizationResult | null>(null);
  const [applyMethod, setApplyMethod] = useState<"domain_connect" | "cloudflare" | "manual" | null>(null);
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  // Start analysis on mount
  useEffect(() => {
    runAnalysis();
  }, []);

  // Analysis step: Resolve and optimize SPF
  const runAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Resolve current SPF to get full data
      const resolveData = await api.resolveSpf(domainId);
      setResolvedData(resolveData);

      // Step 2: Run AI optimization
      const optimizeData = await api.optimizeSpf(domainId, {
        current_spf: currentSpf,
        resolved_data: resolveData,
      });

      setOptimization(optimizeData);
      setStep("review");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [domainId, currentSpf]);

  // Apply the optimized SPF record
  const handleApply = useCallback(async () => {
    if (!optimization || !applyMethod) return;

    setApplying(true);
    setError(null);

    try {
      if (applyMethod === "domain_connect") {
        // Get Domain Connect URL and redirect
        const dcRes = await api.getDomainConnectSpfUrl({
          domain: domainName,
          spf_record: optimization.suggested_spf,
          redirect_uri: window.location.href,
          state: `spf_optimize_${domainId}`,
        });

        // Open in new tab or redirect
        window.open(dcRes.url, "_blank");
        setStep("confirmation");
      } else if (applyMethod === "cloudflare") {
        // Use Cloudflare DNS API
        await api.applySpf(domainId, {
          optimization_id: optimization.optimization_id,
          method: "cloudflare",
        });

        setStep("confirmation");
      } else {
        // Manual - just show success
        setStep("confirmation");
      }

      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to apply changes");
    } finally {
      setApplying(false);
    }
  }, [optimization, applyMethod, domainName, domainId, onSuccess]);

  // Copy to clipboard helper
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  // Progress indicator
  const steps: { id: WizardStep; label: string }[] = [
    { id: "analysis", label: "Analyze" },
    { id: "review", label: "Review" },
    { id: "apply", label: "Apply" },
    { id: "confirmation", label: "Done" },
  ];

  const currentStepIndex = steps.findIndex((s) => s.id === step);

  return (
    <Modal open onOpenChange={(open) => !open && onClose()}>
      <Modal.Content size="xl" hideCloseButton ariaLabel="SPF optimization wizard">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-900 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Sparkles className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                SPF Optimization Wizard
              </h2>
              <p className="text-sm text-slate-300">{domainName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-300" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between">
            {steps.map((s, idx) => (
              <div key={s.id} className="flex items-center">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      idx < currentStepIndex
                        ? "bg-amber-500 text-slate-900"
                        : idx === currentStepIndex
                          ? "bg-slate-800 text-white"
                          : "bg-slate-200 text-slate-500"
                    }`}
                  >
                    {idx < currentStepIndex ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      idx + 1
                    )}
                  </div>
                  <span
                    className={`text-sm font-medium ${
                      idx <= currentStepIndex ? "text-slate-800" : "text-slate-400"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <ChevronRight className="w-5 h-5 text-slate-300 mx-4" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error display */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-700">Error</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {/* Step Content */}
          {step === "analysis" && (
            <AnalysisStep loading={loading} currentSpf={currentSpf} />
          )}

          {step === "review" && optimization && (
            <ReviewStep
              optimization={optimization}
              currentSpf={currentSpf}
              resolvedData={resolvedData}
              copied={copied}
              onCopy={copyToClipboard}
            />
          )}

          {step === "apply" && optimization && (
            <ApplyStep
              optimization={optimization}
              domainName={domainName}
              applyMethod={applyMethod}
              onSelectMethod={setApplyMethod}
              copied={copied}
              onCopy={copyToClipboard}
            />
          )}

          {step === "confirmation" && optimization && (
            <ConfirmationStep
              optimization={optimization}
              applyMethod={applyMethod}
              domainName={domainName}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <div>
            {step === "review" && (
              <button
                onClick={() => runAnalysis()}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Re-analyze
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {step === "review" && (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep("apply")}
                  className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors flex items-center gap-2"
                >
                  Continue
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}

            {step === "apply" && (
              <>
                <button
                  onClick={() => setStep("review")}
                  className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors font-medium"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
                <button
                  onClick={handleApply}
                  disabled={!applyMethod || applying}
                  className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {applying ? (
                    <>
                      <div className="w-4 h-4 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      {applyMethod === "manual" ? "Done" : "Apply Changes"}
                    </>
                  )}
                </button>
              </>
            )}

            {step === "confirmation" && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
}

// ============================================
// Step Components
// ============================================

function AnalysisStep({
  loading,
  currentSpf,
}: {
  loading: boolean;
  currentSpf: string | null;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      {loading ? (
        <>
          <div className="relative mb-6">
            <div className="w-16 h-16 border-4 border-amber-200 rounded-full" />
            <div className="absolute inset-0 w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
            <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-amber-500" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            Analyzing your SPF record
          </h3>
          <p className="text-slate-500 text-center max-w-md">
            Our AI is examining your current SPF configuration, resolving includes,
            and generating an optimized record.
          </p>
          <div className="mt-6 space-y-2 text-sm text-slate-500">
            <p className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              Resolving DNS lookups
            </p>
            <p className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              Analyzing mechanisms
            </p>
            <p className="flex items-center gap-2 text-slate-400">
              <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
              Generating optimizations
            </p>
          </div>
        </>
      ) : (
        <>
          <Shield className="w-16 h-16 text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            Ready to Optimize
          </h3>
          {currentSpf ? (
            <div className="text-center">
              <p className="text-slate-500 mb-4">
                Current SPF record detected:
              </p>
              <code className="block p-3 bg-slate-100 rounded-lg text-sm font-mono text-slate-700 max-w-md overflow-x-auto">
                {currentSpf}
              </code>
            </div>
          ) : (
            <p className="text-slate-500">
              No SPF record found. We'll help you create one.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function ReviewStep({
  optimization,
  currentSpf,
  resolvedData,
  copied,
  onCopy,
}: {
  optimization: SpfOptimizationResult;
  currentSpf: string | null;
  resolvedData: SpfResolveResult | null;
  copied: string | null;
  onCopy: (text: string, label: string) => void;
}) {
  const riskConfig = RISK_CONFIG[optimization.risk_level];
  const RiskIcon = riskConfig.icon;

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      {optimization.explanation && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <h4 className="font-semibold text-slate-800 mb-2">
            {optimization.explanation.summary}
          </h4>
        </div>
      )}

      {/* Risk Level Badge */}
      <div className={`p-4 rounded-lg border ${riskConfig.bgColor} ${riskConfig.color}`}>
        <div className="flex items-center gap-3">
          <RiskIcon className="w-5 h-5" />
          <div>
            <p className="font-semibold">{riskConfig.label} Changes</p>
            <p className="text-sm opacity-90">{riskConfig.description}</p>
          </div>
        </div>
      </div>

      {/* DNS Lookup Count */}
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-slate-400" />
          <span className="text-slate-600">DNS Lookups</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400">{optimization.lookup_count?.before || resolvedData?.lookup_count || "?"}</span>
          <ArrowRight className="w-4 h-4 text-slate-400" />
          <span
            className={`font-bold ${
              (optimization.lookup_count?.after || 0) <= 8
                ? "text-emerald-700"
                : (optimization.lookup_count?.after || 0) <= 10
                  ? "text-amber-600"
                  : "text-red-600"
            }`}
          >
            {optimization.lookup_count?.after || "?"}
          </span>
          <span className="text-slate-400">/ 10</span>
        </div>
      </div>

      {/* Diff View */}
      <div className="space-y-3">
        <h4 className="font-medium text-slate-800">Record Comparison</h4>

        {/* Current Record */}
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-red-700">Current</span>
          </div>
          <code className="block text-sm font-mono text-red-700 break-all">
            {currentSpf || "No SPF record"}
          </code>
        </div>

        {/* Suggested Record */}
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-emerald-700">Suggested</span>
            <button
              onClick={() => onCopy(optimization.suggested_spf, "suggested")}
              className="flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-700"
            >
              {copied === "suggested" ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy
                </>
              )}
            </button>
          </div>
          <code className="block text-sm font-mono text-emerald-700 break-all">
            {optimization.suggested_spf}
          </code>
        </div>
      </div>

      {/* Changes List */}
      {optimization.diff?.changes && optimization.diff.changes.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-slate-800">Changes Made</h4>
          <div className="space-y-2">
            {optimization.diff.changes.map((change, idx) => {
              const config = CHANGE_TYPE_CONFIG[change.type] || DEFAULT_CHANGE_CONFIG;
              const Icon = config.icon;
              return (
                <div
                  key={idx}
                  className={`flex items-start gap-3 p-3 rounded-lg ${config.bgColor}`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 ${config.color}`} />
                  <div>
                    <p className={`font-medium ${config.color}`}>
                      {config.label}: <code className="font-mono">{change.mechanism}</code>
                    </p>
                    <p className="text-sm text-slate-600">{change.reason}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Key Changes from Explanation */}
      {optimization.explanation?.key_changes && optimization.explanation.key_changes.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-slate-800">Key Changes</h4>
          <ul className="space-y-2">
            {optimization.explanation.key_changes.map((point, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 flex-shrink-0" />
                {point}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Warnings */}
      {optimization.validation?.warnings && optimization.validation.warnings.length > 0 && (
        <div className="space-y-2">
          {optimization.validation.warnings.map((warning, idx) => (
            <div
              key={idx}
              className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg"
            >
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{warning}</p>
            </div>
          ))}
        </div>
      )}

      {/* TXT Split Warning */}
      {optimization.suggested_spf.length > 255 && (
        <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            This record exceeds 255 characters and will need to be split into
            multiple TXT record strings. Most DNS providers handle this automatically.
          </p>
        </div>
      )}

      {/* Technical Notes */}
      {optimization.explanation?.technical_notes && (
        <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600">{optimization.explanation.technical_notes}</p>
        </div>
      )}
    </div>
  );
}

function ApplyStep({
  optimization,
  domainName,
  applyMethod,
  onSelectMethod,
  copied,
  onCopy,
}: {
  optimization: SpfOptimizationResult;
  domainName: string;
  applyMethod: "domain_connect" | "cloudflare" | "manual" | null;
  onSelectMethod: (method: "domain_connect" | "cloudflare" | "manual") => void;
  copied: string | null;
  onCopy: (text: string, label: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-2">
          How would you like to apply this change?
        </h3>
        <p className="text-slate-500">
          Choose your preferred method to update the SPF record for {domainName}
        </p>
      </div>

      {/* Apply Methods */}
      <div className="space-y-3">
        {/* Domain Connect */}
        <button
          onClick={() => onSelectMethod("domain_connect")}
          className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
            applyMethod === "domain_connect"
              ? "border-amber-500 bg-amber-50"
              : "border-slate-200 hover:border-slate-300"
          }`}
        >
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${applyMethod === "domain_connect" ? "bg-amber-100" : "bg-slate-100"}`}>
              <Zap className={`w-5 h-5 ${applyMethod === "domain_connect" ? "text-amber-600" : "text-slate-500"}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-medium text-slate-800">Domain Connect</p>
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                  Recommended
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                One-click setup via Cloudflare. Opens their authorize page.
              </p>
            </div>
            {applyMethod === "domain_connect" && (
              <CheckCircle2 className="w-5 h-5 text-amber-600" />
            )}
          </div>
        </button>

        {/* Cloudflare DNS API */}
        <button
          onClick={() => onSelectMethod("cloudflare")}
          className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
            applyMethod === "cloudflare"
              ? "border-amber-500 bg-amber-50"
              : "border-slate-200 hover:border-slate-300"
          }`}
        >
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${applyMethod === "cloudflare" ? "bg-amber-100" : "bg-slate-100"}`}>
              <Shield className={`w-5 h-5 ${applyMethod === "cloudflare" ? "text-amber-600" : "text-slate-500"}`} />
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-800">Cloudflare DNS API</p>
              <p className="text-sm text-slate-500 mt-1">
                Apply directly if your domain uses Cloudflare DNS and is connected.
              </p>
            </div>
            {applyMethod === "cloudflare" && (
              <CheckCircle2 className="w-5 h-5 text-amber-600" />
            )}
          </div>
        </button>

        {/* Manual */}
        <button
          onClick={() => onSelectMethod("manual")}
          className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
            applyMethod === "manual"
              ? "border-amber-500 bg-amber-50"
              : "border-slate-200 hover:border-slate-300"
          }`}
        >
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${applyMethod === "manual" ? "bg-amber-100" : "bg-slate-100"}`}>
              <FileText className={`w-5 h-5 ${applyMethod === "manual" ? "text-amber-600" : "text-slate-500"}`} />
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-800">Manual Update</p>
              <p className="text-sm text-slate-500 mt-1">
                Copy the record and update your DNS provider manually.
              </p>
            </div>
            {applyMethod === "manual" && (
              <CheckCircle2 className="w-5 h-5 text-amber-600" />
            )}
          </div>
        </button>
      </div>

      {/* Manual Instructions */}
      {applyMethod === "manual" && (
        <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <h4 className="font-medium text-slate-800">Manual Update Instructions</h4>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-slate-600">Record Type</span>
            </div>
            <code className="block p-3 bg-white border border-slate-200 rounded-lg text-sm font-mono">
              TXT
            </code>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-slate-600">Host / Name</span>
              <button
                onClick={() => onCopy("@", "host")}
                className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700"
              >
                {copied === "host" ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <code className="block p-3 bg-white border border-slate-200 rounded-lg text-sm font-mono">
              @ (or {domainName})
            </code>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-slate-600">Value</span>
              <button
                onClick={() => onCopy(optimization.suggested_spf, "value")}
                className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700"
              >
                {copied === "value" ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <code className="block p-3 bg-white border border-slate-200 rounded-lg text-sm font-mono break-all">
              {optimization.suggested_spf}
            </code>
          </div>

          <div className="flex items-start gap-2 text-sm text-slate-500">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              Replace your existing SPF record with this new one. Do not add
              multiple SPF records for the same domain.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfirmationStep({
  optimization,
  applyMethod,
  domainName,
}: {
  optimization: SpfOptimizationResult;
  applyMethod: "domain_connect" | "cloudflare" | "manual" | null;
  domainName: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
        <CheckCircle2 className="w-8 h-8 text-emerald-700" />
      </div>

      <h3 className="text-xl font-semibold text-slate-800 mb-2">
        {applyMethod === "manual"
          ? "Optimization Complete"
          : applyMethod === "domain_connect"
            ? "Domain Connect Initiated"
            : "SPF Record Updated"}
      </h3>

      <p className="text-slate-500 max-w-md mb-6">
        {applyMethod === "manual" ? (
          <>
            Your optimized SPF record is ready. Copy the record above and update
            your DNS provider. Changes may take up to 48 hours to propagate.
          </>
        ) : applyMethod === "domain_connect" ? (
          <>
            Complete the authorization in the Cloudflare tab that opened.
            Your SPF record will be updated once you approve the changes.
          </>
        ) : (
          <>
            Your SPF record for <strong>{domainName}</strong> has been updated.
            DNS changes may take up to 48 hours to propagate globally.
          </>
        )}
      </p>

      {/* Summary Stats */}
      <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-lg">
        <div className="text-center">
          <p className="text-2xl font-bold text-emerald-700">{optimization.lookup_count?.after || "?"}</p>
          <p className="text-sm text-slate-500">DNS Lookups</p>
        </div>
        <div className="w-px h-10 bg-slate-200" />
        <div className="text-center">
          <p className="text-2xl font-bold text-slate-800">{optimization.diff?.changes?.length || 0}</p>
          <p className="text-sm text-slate-500">Changes Made</p>
        </div>
        <div className="w-px h-10 bg-slate-200" />
        <div className="text-center">
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
              RISK_CONFIG[optimization.risk_level].color
            }`}
          >
            {RISK_CONFIG[optimization.risk_level].label}
          </span>
          <p className="text-sm text-slate-500 mt-1">Risk Level</p>
        </div>
      </div>

      {/* Next Steps */}
      <div className="mt-8 text-left w-full max-w-md">
        <h4 className="font-medium text-slate-800 mb-3">Next Steps</h4>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2">
            <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2" />
            Monitor email delivery over the next 24-48 hours
          </li>
          <li className="flex items-start gap-2">
            <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2" />
            Check DMARC reports for any new failures
          </li>
          <li className="flex items-start gap-2">
            <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2" />
            Verify the change with a DNS lookup tool
          </li>
        </ul>
      </div>
    </div>
  );
}